import { getErrorJson } from '@mm/utils';
import { Dispatch } from 'redux';
import PromotionsController from '../controllers/PromotionsController';
import promotionsHandler from '../errorHandlers/promotions-handler';
import modalActions from '../modules/modalsState/actions';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import ChatbotCRUDActions from './ChatbotCRUDActions';
import connectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';

export enum Partners {
  MobileMonkey = 'Customers.ai',
  AppSumoPro = 'AppSumoPro',
  GreeDeals = 'GreeDeals',
  Comps = 'Comps',
  Warrior = 'Warrior',
}

class PromotionsCRUDActions extends CrudActionContainer {
  setStatus: string;

  constructor() {
    super('PROMOTIONS');
    this.setStatus = this.makeActionKey('SET_STATUS');
  }

  clearStatus = (dispatch: Dispatch<StoreState>) => {
    dispatch({
      type: this.setStatus,
      isStatusValid: undefined,
    });
  };

  getPromotions(companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await PromotionsController.getPromotions(companyId);
        dispatch(this.retrievedAll(response));
        return response;
      } catch (error) {
        return promotionsHandler(dispatch)(error);
      }
    };
  }

  attachInstachampPromo(
    companyId: number,
    facebookPageId: number,
    promoCode: string,
    reloadPage: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await PromotionsController.attachInstachampPromo(
          companyId,
          facebookPageId,
          promoCode,
        );
        if (reloadPage) {
          await dispatch(
            connectedPageCRUDActions.retrievePage(facebookPageId, true, true),
          );
        }
        return result;
      } catch (e) {
        if (e instanceof Response && [400, 404].indexOf(e.status) !== -1) {
          throw e;
        }
        return promotionsHandler(dispatch)(e);
      }
    };
  }

  addPromoToUser(companyId: number, promoCode: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await PromotionsController.addPromoToUser(
          companyId,
          promoCode,
        );
        return response;
      } catch (error) {
        if (error instanceof Response) {
          if (error.status === 404) {
            dispatch(
              modalActions.openPrettyErrorModal({
                title: 'Unable to apply Promo Code',
                errorMessage: 'Invalid Promo Code',
              }),
            );
          } else if (error.status === 400) {
            const responseErrorBody = await getErrorJson(error);
            const errorMessage = responseErrorBody.errors
              ? responseErrorBody.errors[0]
              : '';
            dispatch(
              modalActions.openPrettyErrorModal({
                title: 'Unable to apply Promo Code',
                errorMessage,
              }),
            );
          } else {
            return promotionsHandler(dispatch)(error);
          }
        } else {
          return promotionsHandler(dispatch)(error);
        }
      }
    };
  }

  connect(id: number, companyId: number, facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await PromotionsController.connect(
          id,
          companyId,
          facebookPageId,
        );
        dispatch(
          connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
            withSubscription: true,
            withDowngradePending: true,
            withMessagesStatistic: true,
          }),
        );
        dispatch(this.getPromotions(companyId));
        return response;
      } catch (error) {
        return promotionsHandler(dispatch)(error);
      }
    };
  }

  disconnect(
    id: number,
    companyId: number,
    facebookPageId: number,
    pageNumber?: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await PromotionsController.disconnect(
          id,
          companyId,
          facebookPageId,
        );
        dispatch(ChatbotCRUDActions.retrieveAll(facebookPageId));
        dispatch(this.getPromotions(companyId));
        dispatch(this.retrieved(response));
        dispatch(this.unbusy());
        return response;
      } catch (error) {
        return promotionsHandler(dispatch)(error);
      }
    };
  }
}

const promotionsCRUDActions = new PromotionsCRUDActions();

export const usePromotionsCRUDActions = getActionHookBuilder(
  promotionsCRUDActions,
  ['attachInstachampPromo'],
);
export default promotionsCRUDActions;
